import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Copy, Inbox, MailPlus, Send, Ticket, X } from 'lucide-react'
import { callAuth } from '@/lib/authClient'
import { buildWebInviteMessage } from '@/lib/inviteLinks'
import {
  approveAccessRequest,
  deactivateInvite,
  fetchAccessRequests,
  fetchManagementInvites,
  rejectAccessRequest,
  type ManagementAccessRequest,
  type ManagementInvite,
} from '@/services/managementAccessService'

/**
 * Обработка доступа в УК на экране «Пользователи»: входящие заявки и уже
 * выданные, но ещё не принятые приглашения.
 *
 * Почему приглашения живут здесь отдельной секцией, а не строками общего списка:
 * приглашённый — ещё не пользователь, строки в `users` у него нет вовсе.
 *
 * Гейт — серверный (`is_super_admin()` внутри каждой RPC). Компонент монтируется
 * только на супер-админском экране, но на это не полагается.
 */

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('ru-RU')
}

function InviteStatus({ invite }: { invite: ManagementInvite }) {
  if (!invite.is_active) return <span className="text-muted-foreground">Деактивирован</span>
  if (invite.is_expired) return <span className="text-red-600">Истёк</span>
  if (invite.is_exhausted) return <span className="text-amber-700">Использован</span>
  return <span className="text-emerald-700">Ждёт входа</span>
}

export const ManagementAccessInbox: React.FC = () => {
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [rejecting, setRejecting] = useState<{ id: number; note: string } | null>(null)
  const [busyInviteId, setBusyInviteId] = useState<number | null>(null)

  const requestsQuery = useQuery({
    queryKey: ['management-access-requests'],
    queryFn: () => fetchAccessRequests(),
  })
  const invitesQuery = useQuery({
    queryKey: ['management-invites'],
    queryFn: () => fetchManagementInvites(),
  })

  const pending = useMemo(
    () => (requestsQuery.data ?? []).filter(r => r.status === 'PENDING'),
    [requestsQuery.data]
  )
  // Ждущие входа: активные, не истёкшие и не исчерпанные. Остальные — история,
  // на экране обработки от неё только шум.
  const waiting = useMemo(
    () => (invitesQuery.data ?? []).filter(i => i.is_active && !i.is_expired && !i.is_exhausted),
    [invitesQuery.data]
  )
  const loadingError = requestsQuery.error || invitesQuery.error
  const loadingErrorText = loadingError instanceof Error
    ? loadingError.message
    : loadingError ? 'Не удалось загрузить заявки или приглашения' : null

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['management-access-requests'] })
    void queryClient.invalidateQueries({ queryKey: ['management-invites'] })
  }

  const fail = (e: unknown, fallback: string) =>
    setError(e instanceof Error ? e.message : fallback)

  const approve = useMutation({
    mutationFn: (request: ManagementAccessRequest) => approveAccessRequest(request.id),
    onSuccess: async (result, request) => {
      setError(null)
      // Письмо шлём тем же путём, что и раньше при ручной выдаче инвайта:
      // отдельной реализации отправки не заводим.
      try {
        await callAuth('email-request', {
          email: request.target_email,
          invite_token: result.invite_code,
        })
        setNotice(`Приглашение отправлено на ${request.target_email}`)
      } catch {
        setNotice(
          `Инвайт ${result.invite_code} создан, но письмо не ушло — отправьте ссылку вручную`
        )
      }
      refresh()
    },
    onError: e => fail(e, 'Не удалось одобрить заявку'),
  })

  const confirmAndApprove = (request: ManagementAccessRequest) => {
    const person = `${request.target_last_name} ${request.target_first_name}`.trim()
    const access = request.requested_access_level === 'advanced' ? 'расширенный' : 'обычный'
    const warning = request.requested_access_level === 'advanced'
      ? '\n\nПосле входа пользователю станет доступна чувствительная и финансовая информация.'
      : ''
    if (!confirm(
      `Одобрить заявку для ${person} и создать приглашение на ${access} доступ УК?${warning}`
    )) return
    approve.mutate(request)
  }

  const reject = useMutation({
    mutationFn: ({ id, note }: { id: number; note: string }) => rejectAccessRequest(id, note),
    onSuccess: () => {
      setError(null)
      setRejecting(null)
      setNotice('Заявка отклонена, причина видна автору')
      refresh()
    },
    onError: e => fail(e, 'Не удалось отклонить заявку'),
  })

  const resend = async (invite: ManagementInvite) => {
    if (!invite.target_email) return
    setBusyInviteId(invite.id)
    try {
      await callAuth('email-request', {
        email: invite.target_email,
        invite_token: invite.invite_code,
      })
      setNotice(`Ссылка повторно отправлена на ${invite.target_email}`)
      setError(null)
    } catch (e) {
      fail(e, 'Не удалось отправить письмо')
    } finally {
      setBusyInviteId(null)
    }
  }

  const copyLink = async (invite: ManagementInvite) => {
    try {
      await navigator.clipboard.writeText(buildWebInviteMessage(invite.invite_code))
      setNotice('Ссылка скопирована')
    } catch {
      fail(null, 'Буфер обмена недоступен')
    }
  }

  const drop = async (invite: ManagementInvite) => {
    if (!confirm(`Деактивировать приглашение ${invite.invite_code}?`)) return
    setBusyInviteId(invite.id)
    try {
      await deactivateInvite(invite.id)
      setNotice('Приглашение деактивировано')
      setError(null)
      refresh()
    } catch (e) {
      fail(e, 'Не удалось деактивировать приглашение')
    } finally {
      setBusyInviteId(null)
    }
  }

  return (
    <div className="mb-4 space-y-3">
      {(error || notice || loadingErrorText) && (
        <div
          className={`rounded-md border p-2 text-sm ${
            error || loadingErrorText
              ? 'border-red-200 bg-red-50 text-red-700'
              : 'border-emerald-200 bg-emerald-50 text-emerald-800'
          }`}
        >
          {error || loadingErrorText || notice}
        </div>
      )}

      {/* -------------------------------------------------------- заявки */}
      <div className="rounded-md border border-border bg-card">
        <div className="flex items-center gap-2 border-b border-border p-3">
          <Inbox className="h-4 w-4 text-primary" />
          <span className="text-sm font-semibold">Заявки на доступ</span>
          {pending.length > 0 && (
            <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-700">
              {pending.length} ждут решения
            </span>
          )}
        </div>

        {requestsQuery.isLoading ? (
          <p className="p-3 text-sm text-muted-foreground">Загрузка…</p>
        ) : pending.length === 0 ? (
          <p className="p-3 text-sm text-muted-foreground">Открытых заявок нет.</p>
        ) : (
          <div className="divide-y divide-border">
            {pending.map(request => (
              <div key={request.id} className="p-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-foreground">
                      {request.target_last_name} {request.target_first_name} — {request.target_position}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {request.target_email} ·{' '}
                      {request.requested_access_level === 'advanced' ? 'расширенный' : 'стандартный'} доступ
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      Просит: {request.requested_by_name ?? '—'} · {formatDate(request.created_at)}
                    </div>
                    {request.reason && (
                      <div className="mt-1 text-xs text-foreground">{request.reason}</div>
                    )}
                  </div>

                  <div className="flex shrink-0 gap-1.5">
                    <button
                      type="button"
                      disabled={approve.isPending}
                      onClick={() => confirmAndApprove(request)}
                      className="inline-flex h-8 items-center gap-1 rounded-md bg-primary px-2.5 text-xs font-medium text-primary-foreground disabled:opacity-50"
                    >
                      <Check className="h-3.5 w-3.5" />
                      Одобрить
                    </button>
                    <button
                      type="button"
                      onClick={() => setRejecting({ id: request.id, note: '' })}
                      className="inline-flex h-8 items-center gap-1 rounded-md border border-border px-2.5 text-xs text-muted-foreground hover:bg-muted"
                    >
                      <X className="h-3.5 w-3.5" />
                      Отклонить
                    </button>
                  </div>
                </div>

                {rejecting?.id === request.id && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    <input
                      value={rejecting.note}
                      onChange={e => setRejecting({ id: request.id, note: e.target.value })}
                      placeholder="Причина отказа — её увидит автор заявки"
                      className="h-8 min-w-0 flex-1 rounded-md border border-border bg-background px-2 text-xs outline-none focus:border-slate-400"
                    />
                    <button
                      type="button"
                      disabled={rejecting.note.trim().length < 3 || reject.isPending}
                      onClick={() => reject.mutate({ id: request.id, note: rejecting.note })}
                      className="inline-flex h-8 items-center rounded-md border border-border px-2.5 text-xs disabled:opacity-50"
                    >
                      Отклонить
                    </button>
                    <button
                      type="button"
                      onClick={() => setRejecting(null)}
                      className="inline-flex h-8 items-center rounded-md px-2 text-xs text-muted-foreground"
                    >
                      Отмена
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ------------------------------------------------- приглашения */}
      <div className="rounded-md border border-border bg-card">
        <div className="flex items-center gap-2 border-b border-border p-3">
          <Ticket className="h-4 w-4 text-primary" />
          <span className="text-sm font-semibold">Приглашены, ещё не вошли</span>
          {waiting.length > 0 && (
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
              {waiting.length}
            </span>
          )}
        </div>

        {invitesQuery.isLoading ? (
          <p className="p-3 text-sm text-muted-foreground">Загрузка…</p>
        ) : waiting.length === 0 ? (
          <p className="p-3 text-sm text-muted-foreground">Никто не ждёт входа.</p>
        ) : (
          <div className="divide-y divide-border">
            {waiting.map(invite => (
              <div key={invite.id} className="flex flex-wrap items-center justify-between gap-2 p-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-foreground">
                    {invite.target_last_name || invite.target_first_name
                      ? `${invite.target_last_name ?? ''} ${invite.target_first_name ?? ''}`.trim()
                      : 'Приглашение без адресата'}
                    {invite.target_position ? ` — ${invite.target_position}` : ''}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {invite.target_email ?? 'ссылку можно передать любому'} · код {invite.invite_code} ·
                    до {formatDate(invite.valid_until)} · <InviteStatus invite={invite} />
                  </div>
                </div>

                <div className="flex shrink-0 gap-1.5">
                  {invite.target_email && (
                    <button
                      type="button"
                      disabled={busyInviteId === invite.id}
                      onClick={() => void resend(invite)}
                      className="inline-flex h-8 items-center gap-1 rounded-md border border-border px-2.5 text-xs text-muted-foreground hover:bg-muted disabled:opacity-50"
                    >
                      <Send className="h-3.5 w-3.5" />
                      Отправить снова
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => void copyLink(invite)}
                    className="inline-flex h-8 items-center gap-1 rounded-md border border-border px-2.5 text-xs text-muted-foreground hover:bg-muted"
                  >
                    <Copy className="h-3.5 w-3.5" />
                    Ссылка
                  </button>
                  <button
                    type="button"
                    disabled={busyInviteId === invite.id}
                    onClick={() => void drop(invite)}
                    className="inline-flex h-8 items-center gap-1 rounded-md border border-border px-2.5 text-xs text-red-600 hover:bg-red-50 disabled:opacity-50"
                  >
                    <X className="h-3.5 w-3.5" />
                    Отозвать
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="border-t border-border p-3 text-xs text-muted-foreground">
          <MailPlus className="mr-1 inline h-3.5 w-3.5" />
          Приглашения выдаются по заявкам сотрудников — форма заявки на экране «Заявка на доступ в УК».
        </div>
      </div>
    </div>
  )
}
